import {
  Button,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  DialogTrigger,
} from '@ibsys/kit';
import { IconButton } from '../IconButton';
import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/shared/ui';

/** Доступное имя кнопке задаёт `label`, поэтому своё триггер задать не может — иначе имя и подсказка разойдутся */
type ButtonProps = Omit<React.ComponentProps<typeof Button>, 'aria-label'>;

type Props = {
  className?: string;
  onConfirm: () => void | Promise<unknown>;
  isLoadingConfirm?: boolean;
  title: React.ReactNode;
  description?: React.ReactNode;
} & (
  | {
      /**
       * Название действия: и доступное имя кнопки-значка, и её подсказка. Называет предмет действия
       * («Удалить шаблон выгрузки»), потому что один и тот же значок в разных местах удаляет разное.
       */
      label: string;
      trigger?: Omit<ButtonProps, 'children'>;
    }
  | {
      /** Кнопку с видимой подписью называет сама подпись — название действия ей не задают */
      label?: never;
      trigger: ButtonProps & { children: React.ReactNode };
    }
);

/**
 * Кнопка деструктивного действия с диалогом подтверждения.
 * По умолчанию — иконка корзины; название действия задаёт вызывающий.
 */
export const ConfirmationButton: FC<Props> = (props) => {
  const { className, onConfirm, title, description, isLoadingConfirm } = props;

  const [open, setOpen] = useState(false);

  const { t } = useTranslation('common');

  const handleConfirm = async () => {
    await onConfirm();
    setOpen(false);
  };

  // Кнопка без подписи остаётся иконочной: название действия служит ей и доступным именем, и
  // подсказкой — читаться глазами оно должно так же, как с экрана. Подписанной кнопке не нужно
  // ни то, ни другое — её называет сама подпись.
  const dialogTrigger =
    props.label === undefined ? (
      <DialogTrigger asChild>
        <Button
          icon={<Icon type={'trash'} />}
          variant={'ghost'}
          color={'destructive'}
          {...props.trigger}
          className={className}
        />
      </DialogTrigger>
    ) : (
      // Кнопка-значок собирается общим компонентом вместе со своим триггером: пара «подсказка +
      // доступное имя» задана в нём одном
      <IconButton
        icon={<Icon type={'trash'} />}
        color={'destructive'}
        {...props.trigger}
        label={props.label}
        trigger={DialogTrigger}
        className={className}
      />
    );

  return (
    <DialogRoot onOpenChange={setOpen} open={open}>
      {dialogTrigger}
      <DialogContent className={'w-max'}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        {description && <DialogDescription>{description}</DialogDescription>}
        <DialogFooter>
          <DialogClose asChild>
            <Button variant={'ghost'}>{t('cancel')}</Button>
          </DialogClose>
          <Button color={'destructive'} variant={'outline'} onClick={handleConfirm} isLoading={isLoadingConfirm}>
            {t('confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  );
};
